import React, { memo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button } from '@mafc/trolly';
import { useMount } from '@mafc/hooks';
import { ArrowLeft, RightArrow } from '@mafc/icon';

import { useHistory } from 'react-router-dom';

import { isRTL } from 'config/localisation';

import useCartShipments from 'hooks/useCartShipments.hook';

import useReferrerUri from './hooks/useReferrerUri.hook';
import useFoodMinimumOrder from 'pages/Cart/components/FoodMinimumOrder/hooks/useFoodMinimumOrder.hook';
import useReasonCode from 'hooks/useReasonCode';

import CartCheckoutContainer from 'common/CartCheckoutContainer';
import CheckoutOrderSummary from './components/CheckoutOrderSummary';
import DeliverTo from './components/Delivery';
import Shipments from './components/Shipments';
import Payment from './components/Payment';
import useGTMVirtualPageTrakingEffect from 'pages/Cart/hooks/useGTMVirtualPageTrakingEffect';
import useDeliverySlot from 'common/DeliverySlot/hooks/useDeliverySlot.hook';
import { useCallback } from 'react';
import SingleInputField from './components/SingleInputField';
import useAction from 'hooks/useAction';
import { updateNIC, resetNIC } from 'common/User/redux/User.actions';
import { INicField } from 'common/User/redux/User.reducer';
import { useDispatch, useSelector } from 'react-redux';
import usePaymentErrorHandler from './helpers/usePaymentErrorHandler';
import { getEarnedPoints, resetPackingChargesCartData } from 'pages/Cart/redux/actions';
import { setStepStatus } from './redux/actions/stepsProgress.action';
import { resetDeliverTo } from './redux/actions/delivery.action';
import { EStepsProgress } from './interfaces/stepsProgress.interface';
import { resetCNCShipment } from './redux/actions/cnc.action';
import { hideNotification } from 'common/Notification/redux/notification.action';
import useUser from 'services/user/hooks/useUser.hook';
import CheckoutLoader from './components/CheckoutLoader';
import { ICheckoutState } from './redux/reducer';
import OrderSummaryLoader from 'common/OrderSummary/components/OrderSummaryLoader';
import { storePaymentDetails, storePaymentMode } from './redux/actions/payment.action';
import { resetPromo } from './redux/actions/promo.action';
import { IAppState } from 'config/redux/reducers';
import SingleUsePackage from 'common/SingleUsePackage/SingleUsePackage';
import { isUndefined } from 'utils/helpers.util';
import { FBCModal } from '@mafc/common';
import useNewC4Journey from 'hooks/useNewC4Journey';
import useFetchApigeeLabel from 'common/ApigeeLabels/hooks/useFetchApigeeLabel';
import LoyaltyAndDiscount from './components/LoyaltyAndDiscount';
import BinDiscountBanner from 'common/BinDiscountBanner';
import useDriverTips from 'hooks/useDriverTips';
import DriverTips from './components/DriverTips';
import { newRelicCustomEvent } from 'utils/newRelicTracker';
import { NEW_RELIC_EVENT } from './constants/checkout.constant';
import useCheckoutGTM from './components/Shipments/hooks/useCheckoutGTM.hook';
import { setOrderTotalCurrency } from 'common/OrderSummary/redux/orderSummary.action';
interface Props {
  location?: Location;
}

const Checkout: React.FC<Props> = ({ location }) => {
  const { t } = useTranslation();
  const rtl = isRTL();
  const CartContainer = rtl ? CartCheckoutContainer.RightBox : CartCheckoutContainer.LeftBox;
  const dispatch = useDispatch();
  useFetchApigeeLabel();
  const history: { location: { state: { [key: string]: any } } } = useHistory();
  const [updateNICConnected] = useAction(updateNIC);
  const [resetNICConnected] = useAction(resetNIC);
  const { nicField = {} as INicField, countryConfiguration = {} } =
    useSelector((state: IAppState) => ({
      nicField: state.user.nicField,
      countryConfiguration: state.appConfig.countryConfiguration,
    })) || ({} as any);
  const { cart: { isBinSupported = '' } = {} } = countryConfiguration;

  const { stepsProgress } = useSelector((state: { checkout: ICheckoutState }) => state.checkout);

  const isNewC4Journey = useNewC4Journey();
  const { areTipsConfigured, driverTips } = useDriverTips();

  const { deliverTo } = stepsProgress;
  const { userData } = useSelector((state: IAppState) => ({
    userData: state.userData,
  }));
  const updateNICField = useCallback(
    (text: string) => {
      updateNICConnected(text);
    },
    [updateNICConnected]
  );
  const isFirstRender = useRef(true);

  useUser(isFirstRender.current);
  usePaymentErrorHandler();

  useReasonCode(location ? location.search : '');
  // TODO: implement page view as per the latest react-router version
  // https://reacttraining.com/react-router/web/api/Hooks/uselocation
  // hits user api
  useGTMVirtualPageTrakingEffect('checkout');

  // Fetches user's cart
  const {
    cartState: { cartData },
    isCartAPICompleted,
    isLoading,
    isCartEmpty,
  } = useCartShipments(false);
  useCheckoutGTM(cartData);

  // CCX-610: Below threshold check + auto redirect
  // TODO: This logic needs to refined for new C4 Journey as well, as of now not running this logic for new C4 journey
  const [onBackToCartHandler] = useReferrerUri();
  const [isBelowThreshold] = useFoodMinimumOrder();
  const isSPCForceLoaded = history.location.state && history.location.state.forceLoadSPC;
  const shouldRedirectToCart = isCartEmpty && !isSPCForceLoaded;
  const expressCheckoutAllowed =
    cartData &&
    cartData.expressCount &&
    cartData.expressCount > 0 &&
    (cartData.store === 'mafgeo' ? !isBelowThreshold : true); // Georgia specific threshold check
  const standardCheckoutAllowed = !isCartEmpty && !isBelowThreshold;
  const isCartCheckoutAllowed = expressCheckoutAllowed || standardCheckoutAllowed;

  useEffect(() => {
    if (!isNewC4Journey && (!isCartCheckoutAllowed || shouldRedirectToCart)) {
      onBackToCartHandler();
    }
  }, [isNewC4Journey, isCartCheckoutAllowed, shouldRedirectToCart, onBackToCartHandler]);

  /**
   * Enable CoachMark when checkout loads
   */
  const [, { showCoachMark }] = useDeliverySlot();
  const showCoachMarkAction = useCallback(() => {
    showCoachMark();
  }, [showCoachMark]);

  useMount(() => {
    showCoachMarkAction();
    dispatch(getEarnedPoints() as any);
  });

  useEffect(() => {
    window.document.title = t('header.checkout');

    // deliverTo information is removed on unmount the checkout component
    // It is removed to have the sequence of set mode should be done
    // only after the address call is done
    return () => {
      dispatch(setStepStatus(EStepsProgress.deliverTo, false));
      dispatch(resetDeliverTo());
      dispatch(resetCNCShipment());
      dispatch(hideNotification());
      // reset payment details
      dispatch(storePaymentMode(''));
      dispatch(storePaymentDetails('', {}));
      dispatch(resetPromo());
      // reset packing charges
      dispatch(resetPackingChargesCartData());
    };
  }, [dispatch, t]);

  useEffect(() => {
    newRelicCustomEvent(NEW_RELIC_EVENT.CHECKOUT_PAGE_VIEWED, {});
    // removed the currency selection(applicable in lebanon) from the session storage
    sessionStorage.removeItem('changeCurrencyToUSD');
    setOrderTotalCurrency({ orderTotalCurrency: '' });
  }, []);

  if (isCartAPICompleted) {
    isFirstRender.current = false;
  }

  if (isCartEmpty) {
    return null;
  }

  const { sustainabilityConsent = null, packingCharges: plasticBagPackingInfo } = cartData;
  const Icon = rtl ? RightArrow : ArrowLeft;

  return window !== window.parent ? null : (
    // @ts-ignore
    <CartCheckoutContainer>
      <Box display={['none', null, null, 'block']} py={3}>
        <Box display="inline-block">
          <Button
            alignItems="center"
            display="flex"
            fontWeight={6}
            onClick={onBackToCartHandler}
            variant="link"
            data-testid="__back-to-cart__"
            height="14px"
          >
            <Icon testid={rtl ? 'RightArrow' : 'ArrowLeft'} size="XS" />
            <Box as="span" ml={2} mr={2}>
              {t('spc.backToCart')}
            </Box>
          </Button>
        </Box>
      </Box>
      <CartCheckoutContainer.Flex
        {...{
          mx: [2, 0],
        }}
      >
        <CartContainer>
          <Box display={isCartAPICompleted && deliverTo ? 'block' : 'none'}>
            {!!(userData.selectedAddress || userData.defaultAddress) ? <DeliverTo /> : <></>}
          </Box>
          {isFirstRender.current || !deliverTo ? (
            <CheckoutLoader />
          ) : (
            <>
              <Shipments cartData={cartData} />

              {areTipsConfigured && <DriverTips tips={driverTips} />}

              <FBCModal />

              {isBinSupported && <BinDiscountBanner hideBinLoading isCheckoutPage />}

              {sustainabilityConsent !== null && !isUndefined(sustainabilityConsent) && (
                <SingleUsePackage
                  sustainabilityConsent={sustainabilityConsent}
                  plasticBagPackingInfo={plasticBagPackingInfo}
                  showHeading
                />
              )}

              <LoyaltyAndDiscount />

              <Payment />
            </>
          )}
        </CartContainer>

        {isCartAPICompleted && deliverTo ? (
          <CheckoutOrderSummary
            isLoading={isLoading}
            Promo={[
              nicField.isRequired && (
                <Box bg="white" mb={3} key="spc-pak-nic-input">
                  <SingleInputField
                    labelText="nicNumberInputText"
                    isLoading={isLoading}
                    errorMessage={nicField.errorReason as string}
                    hasError={nicField.hasError}
                    value={nicField.value}
                    onApplyClick={updateNICField}
                    onRemoveClick={resetNICConnected}
                  />
                </Box>
              ),
            ]}
            nicField={nicField}
          />
        ) : (
          <OrderSummaryLoader hideBinLoading={isCartAPICompleted && deliverTo} />
        )}
      </CartCheckoutContainer.Flex>
    </CartCheckoutContainer>
  );
};

export default memo(Checkout);
